import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { blogPosts, blogSeoCluster } from '../../mocks/blog';

function renderInlineBold(text: string): (string | JSX.Element)[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    /^\*\*[^*]+\*\*$/.test(part)
      ? <strong key={i} className="font-semibold text-gray-900">{part.replace(/\*\*/g, '')}</strong>
      : part
  );
}

function renderContent(content: string): JSX.Element[] {
  return content.split('\n\n').map((block, idx) => {
    const trimmed = block.trim();
    if (!trimmed) return <></>;

    if (/^\*\*[^*]+\*\*$/.test(trimmed)) {
      return (
        <h2 key={idx} className="text-2xl font-bold text-gray-900 mt-10 mb-4">
          {trimmed.replace(/\*\*/g, '')}
        </h2>
      );
    }

    if (trimmed.includes('☐')) {
      const lines = trimmed.split('\n').filter((l) => l.trim());
      return (
        <ul key={idx} className="space-y-2 mb-6 bg-[#F8FAFB] rounded-xl p-5 border border-gray-100">
          {lines.map((line, i) => {
            const clean = line.trim();
            if (clean.startsWith('☐')) {
              return (
                <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <i className="ri-checkbox-blank-line text-emerald-500 flex-shrink-0 mt-0.5 text-base"></i>
                  <span>{clean.replace(/^☐\s*/, '')}</span>
                </li>
              );
            }
            return (
              <li key={i} className="text-sm font-bold text-gray-800 pt-3 pb-1 border-t border-gray-200 first:border-t-0 first:pt-0">
                {clean}
              </li>
            );
          })}
        </ul>
      );
    }

    if (trimmed.startsWith('- ')) {
      const lines = trimmed.split('\n').filter((l) => l.trim().startsWith('- '));
      return (
        <ul key={idx} className="space-y-2 mb-5 pl-2">
          {lines.map((line, i) => (
            <li key={i} className="flex items-start gap-2.5 text-base text-gray-600 leading-relaxed">
              <i className="ri-check-line text-emerald-500 flex-shrink-0 mt-1 text-sm"></i>
              <span>{renderInlineBold(line.replace(/^- /, '').trim())}</span>
            </li>
          ))}
        </ul>
      );
    }

    return (
      <p key={idx} className="mb-5 text-base text-gray-600 leading-relaxed">
        {renderInlineBold(trimmed)}
      </p>
    );
  });
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const isEs = i18n.language === 'es';
  const post = blogPosts.find((p) => p.slug === slug);
  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  const clusterEntry = blogSeoCluster.clusterArticles.find((c) => c.slug === slug);
  const internalLinkedPosts = clusterEntry
    ? clusterEntry.internalLinks
        .map((linkSlug) => blogPosts.find((p) => p.slug === linkSlug))
        .filter(Boolean)
    : [];

  useEffect(() => {
    if (post) {
      document.title = post.seoTitle || `${post.title} | Kailac Blog`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', post.metaDescription || post.excerpt);
    }
  }, [post]);

  const displayTitle = isEs && post?.titleEs ? post.titleEs : post?.title;
  const displayExcerpt = isEs && post?.excerptEs ? post.excerptEs : post?.excerpt;
  const displayContent = isEs && post?.contentEs ? post.contentEs : post?.content;

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('blog_post_not_found')}</h1>
          <Link to="/blog" className="text-[#4F2D63] font-semibold hover:underline cursor-pointer">{t('blog_post_back')}</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const authorName = post.author || 'Kailac Advisory Team';
  const authorTitle = post.authorTitle || 'Expert Financial Advisor';

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />

      {/* Article Hero */}
      <section className="pt-24 pb-0 bg-[#F8F3FB]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-12 pb-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#4F2D63] cursor-pointer">{t('blog_post_home')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/blog" className="hover:text-[#4F2D63] cursor-pointer">{t('blog_post_blog')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-600">{isEs ? post.categoryEs : post.category}</span>
          </nav>

          <div className="flex items-center gap-3 mb-5">
            <span className="text-xs bg-[#F8F3FB] text-[#4F2D63] px-3 py-1.5 rounded-full font-semibold">
              {isEs ? post.categoryEs : post.category}
            </span>
            <span className="text-xs text-gray-400">{post.date}</span>
            <span className="text-xs text-gray-400">•</span>
            <span className="text-xs text-gray-400">{post.readTime}</span>
          </div>

          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight">{displayTitle}</h1>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">{displayExcerpt}</p>

          <div className="flex items-center gap-3 pt-5 border-t border-[#E9D9F2]">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F8F3FB] flex-shrink-0">
              <i className="ri-user-line text-[#4F2D63]"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{authorName}</p>
              <p className="text-xs text-gray-500">{authorTitle}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 -mt-4 mb-0">
        <div className="w-full h-72 lg:h-96 rounded-2xl overflow-hidden">
          <img src={post.image} alt={displayTitle} className="w-full h-full object-cover object-top" />
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-6 lg:px-8 py-14" itemScope itemType="https://schema.org/Article">
        <meta itemProp="headline" content={post.seoTitle || post.title} />
        <meta itemProp="datePublished" content={post.date} />
        <meta itemProp="author" content={authorName} />
        <meta itemProp="publisher" content="Kailac" />

        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
          {displayContent ? renderContent(displayContent) : null}
        </div>

        {/* SEO Cluster Internal Links */}
        {internalLinkedPosts.length > 0 && (
          <div className="mt-10 bg-[#F8F3FB] rounded-2xl p-6 border border-[#E9D9F2]">
            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-book-2-line text-[#4F2D63]"></i>
              {t('blog_post_related')}
            </h3>
            <div className="space-y-3">
              {internalLinkedPosts.map((linkedPost) => linkedPost && (
                <Link
                  key={linkedPost.slug}
                  to={`/blog/${linkedPost.slug}`}
                  className="flex items-center gap-3 group"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#E9D9F2] flex-shrink-0">
                    <i className="ri-article-line text-[#4F2D63] text-sm"></i>
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-[#4F2D63] transition-colors">
                    {isEs && linkedPost.titleEs ? linkedPost.titleEs : linkedPost.title}
                  </span>
                  <i className="ri-arrow-right-line text-[#4F2D63] text-sm ml-auto flex-shrink-0"></i>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Compliance Notice */}
        <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-amber-600 text-base mt-0.5 flex-shrink-0"></i>
            <p className="text-xs text-amber-800 leading-relaxed">
              <strong>{t('blog_post_disclaimer_title')}</strong>{' '}
              {t('blog_post_disclaimer_body')}{' '}
              <Link to="/disclaimer" className="underline cursor-pointer">{t('blog_post_disclaimer_link')}</Link>{' '}
              {t('blog_post_disclaimer_end')}
            </p>
          </div>
        </div>

        {/* CTA in Article */}
        <div className="mt-10 bg-[#F8F3FB] border border-[#E9D9F2] rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('blog_post_cta_title')}</h2>
          <p className="text-gray-500 text-sm mb-6">{t('blog_post_cta_subtitle')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#4F2D63] text-white font-semibold text-sm hover:bg-[#3E234F] transition-colors cursor-pointer whitespace-nowrap">
              {t('blog_post_cta_primary')} <i className="ri-arrow-right-line"></i>
            </Link>
            <Link to="/how-it-works" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-[#E9D9F2] text-gray-700 font-semibold text-sm hover:border-[#4F2D63] transition-colors cursor-pointer whitespace-nowrap">
              {t('blog_post_cta_secondary')}
            </Link>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="py-12 bg-[#F8F3FB] px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-7">{t('blog_post_more')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((relPost) => (
              <Link
                key={relPost.id}
                to={`/blog/${relPost.slug}`}
                className="group bg-white rounded-xl border border-[#E9D9F2] overflow-hidden hover:border-[#DBB6EE] transition-all cursor-pointer block"
              >
                <div className="w-full h-36 overflow-hidden">
                  <img src={relPost.image} alt={relPost.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-5">
                  <span className="text-xs bg-[#F8F3FB] text-[#4F2D63] px-2 py-0.5 rounded font-medium">
                    {isEs ? relPost.categoryEs : relPost.category}
                  </span>
                  <h3 className="text-sm font-bold text-gray-900 mt-2 leading-snug group-hover:text-[#4F2D63] transition-colors">
                    {isEs && relPost.titleEs ? relPost.titleEs : relPost.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
